import { Code2, ShieldCheck, Handshake } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { CtaBanner } from "@/components/CtaBanner";
import { FeatureCard } from "@/components/FeatureCard";

const PILLARS = [
  {
    Icon: Code2,
    title: "Dependable Software",
    text: "We build applications the way we would want them maintained: clear architecture, tested code, and documentation your future team will thank us for.",
  },
  {
    Icon: ShieldCheck,
    title: "Secure Infrastructure",
    text: "Security is built into the process, not bolted on after launch — hardened configurations, monitoring, backups, and disaster recovery planning from day one.",
  },
  {
    Icon: Handshake,
    title: "Long-Term Relationships",
    text: "We stay accountable after launch. Most of our work is ongoing: maintenance, updates, troubleshooting, and growing your systems alongside your business.",
  },
];

export default function AboutPage() {
  return (
    <>
      <Seo
        title="About Tedmo Services — One Accountable Team"
        description="Tedmo Services is a remote-first software and infrastructure partner for small and mid-sized businesses: one accountable team to build, deploy, and support your technology."
        path="/about"
      />

      <section className="py-20 border-b border-line" data-testid="about-hero">
        <div className="max-w-6xl mx-auto px-6">
          <span className="font-mono-sub text-xs text-royal uppercase tracking-wider font-bold">
            About Us
          </span>
          <h1
            data-testid="about-page-title"
            className="font-poster text-4xl sm:text-5xl font-extrabold text-steel mt-2 max-w-3xl leading-tight"
          >
            One accountable team for software and infrastructure
          </h1>
          <p className="text-lg text-ink mt-6 max-w-2xl leading-relaxed">
            We handle the full journey — from building your application to
            deploying it on cloud platforms or your own servers, with security
            built into the process and ongoing support after launch. You have
            one team accountable for your software and infrastructure.
          </p>
          <p className="text-xs font-mono-sub text-mute mt-6">
            Serving SMBs and startups remotely across global operating zones.
          </p>
        </div>
      </section>

      <section className="py-20 max-w-6xl mx-auto px-6" data-testid="about-pillars">
        <Reveal className="mb-12">
          <span className="font-mono-sub text-xs text-royal uppercase tracking-wider font-bold">
            Our Approach
          </span>
          <h2 className="font-poster text-3xl font-bold text-steel mt-2">
            How we work with clients
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {PILLARS.map(({ Icon, title, text }, i) => (
            <Reveal key={title} delay={i * 60}>
              <FeatureCard
                Icon={Icon}
                index={i + 1}
                title={title}
                text={text}
                testId={`pillar-${title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
              />
            </Reveal>
          ))}
        </div>
      </section>

      <CtaBanner title="Work with one accountable team" />
    </>
  );
}
